import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './my_bookmarks.css';
import DashboardFooter from '../../components/DashboardFooter';
import DashboardHeader from '../../components/DashboardHeader';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../auth/firebase';
import { useAuthStore } from '../../auth/state';
import cacheManager from '../../auth/cache_manager';

// # Import bookmark icons
import generalBookmarksIcon from '../../aneo_media/bookmarks/general_bookmarks.png';
import step1Icon from '../../aneo_media/mentee/one.png';
import step2Icon from '../../aneo_media/mentee/two.png';
import calendarIcon from '../../aneo_media/mentee/calendar.png';

function MyBookmarks({ type = "general", isAdmin = false }) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Define bookmark types with their routes and icons
  const bookmarkTypes = {
    general: {
      title: "General Bookmarks",
      icon: generalBookmarksIcon,
      path: isAdmin ? "/admin/bookmarks/general" : "/bookmarks/general"
    },
    calendar: {
      title: "Scheduled Bookmarks",
      icon: calendarIcon,
      path: isAdmin ? "/admin/bookmarks/calendar" : "/bookmarks/calendar"
    },
    step1: {
      title: "Step 1 Library Bookmarks",
      icon: step1Icon,
      path: isAdmin ? "/admin/bookmarks/step1" : "/bookmarks/step1"
    },
    step2: {
      title: "Step 2 Library Bookmarks",
      icon: step2Icon,
      path: isAdmin ? "/admin/bookmarks/step2" : "/bookmarks/step2"
    }
  };

  // # Navigation grid component - now structured as 2x2 grid
  const BookmarkGrid = () => (
    <div className="my_bmk_bookmark-grid">
      <div className="my_bmk_grid-row">
        {/* # First row with first two bookmark cards */}
        <div 
          className="my_bmk_bookmark-card"
          onClick={() => navigate(bookmarkTypes.general.path)}
        >
          <div className="my_bmk_card-icon">
            <img src={bookmarkTypes.general.icon} alt={bookmarkTypes.general.title} />
          </div>
          <h3>{bookmarkTypes.general.title}</h3>
          <p>Click to view bookmarks</p>
        </div>
        
        <div 
          className="my_bmk_bookmark-card"
          onClick={() => navigate(bookmarkTypes.step1.path)}
        >
          <div className="my_bmk_card-icon">
            <img src={bookmarkTypes.step1.icon} alt={bookmarkTypes.step1.title} />
          </div>
          <h3>{bookmarkTypes.step1.title}</h3>
          <p>Click to view bookmarks</p>
        </div>
      </div>
      
      <div className="my_bmk_grid-row">
        {/* # Second row with remaining two bookmark cards */}
        <div 
          className="my_bmk_bookmark-card"
          onClick={() => navigate(bookmarkTypes.step2.path)}
        >
          <div className="my_bmk_card-icon">
            <img src={bookmarkTypes.step2.icon} alt={bookmarkTypes.step2.title} />
          </div>
          <h3>{bookmarkTypes.step2.title}</h3>
          <p>Click to view bookmarks</p>
        </div>
        
        <div 
          className="my_bmk_bookmark-card"
          onClick={() => navigate(bookmarkTypes.calendar.path)}
        >
          <div className="my_bmk_card-icon">
            <img src={bookmarkTypes.calendar.icon} alt={bookmarkTypes.calendar.title} />
          </div>
          <h3>{bookmarkTypes.calendar.title}</h3>
          <p>Click to view scheduled bookmarks</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`my_bmk_bookmarks-container ${isDarkMode ? 'my_bmk_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="my_bmk_bookmarks-main-content">
        <div className="my_bmk_page-title">
          <h1>My Bookmarks</h1>
          <p>Select a category to view your bookmarks</p>
        </div>

        <BookmarkGrid />
      </div>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default MyBookmarks;
